import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Calendar, Clock, User as UserIcon, ArrowLeft, Share2, Twitter, Facebook, Linkedin, Copy } from "lucide-react";
import { toast } from "sonner";
import { PostCard } from "./Blog";
import { renderBlogWithEmbeds } from "../components/AuctionEmbed";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/blog/posts/${slug}`)
      .then(r => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.seo_title || post.title} · AuctionBulls`;
    setMeta("description", post.seo_description || post.excerpt || "");
    setMeta("og:title", post.title, true);
    setMeta("og:description", post.excerpt || "", true);
    if (post.featured_image) setMeta("og:image", post.featured_image, true);
  }, [post]);

  if (notFound) return <div className="max-w-3xl mx-auto py-24 text-center text-slate-500">Article not found. <Link to="/blog" className="text-amber-600 font-semibold">Back to Blog</Link></div>;
  if (!post) return <div className="max-w-3xl mx-auto py-24 text-center text-slate-500">Loading…</div>;

  const copyLink = () => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); };
  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(post.title);

  return (
    <article className="max-w-3xl mx-auto px-4 md:px-6 py-10">
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-600">
        <ArrowLeft className="h-4 w-4" /> All articles
      </Link>

      <div className="mt-6">
        <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">{post.category}</Badge>
      </div>
      <h1 className="mt-4 text-3xl md:text-5xl font-extrabold display-serif leading-tight text-slate-900 dark:text-white">{post.title}</h1>
      {post.excerpt && <p className="mt-4 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">{post.excerpt}</p>}

      <div className="mt-6 flex flex-wrap items-center gap-5 text-xs text-slate-500 border-t border-b border-slate-200 dark:border-slate-800 py-3">
        <span className="inline-flex items-center gap-1"><UserIcon className="h-3 w-3" /> {post.author}</span>
        <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(post.published_at)}</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {post.reading_time} min read</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-slate-400 mr-1">Share:</span>
          <a target="_blank" rel="noopener noreferrer" href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
             aria-label="Share on X" className="h-7 w-7 rounded-full bg-slate-100 dark:bg-slate-800 grid place-items-center hover:bg-slate-900 hover:text-white"><Twitter className="h-3.5 w-3.5" /></a>
          <a target="_blank" rel="noopener noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
             aria-label="Share on Facebook" className="h-7 w-7 rounded-full bg-slate-100 dark:bg-slate-800 grid place-items-center hover:bg-slate-900 hover:text-white"><Facebook className="h-3.5 w-3.5" /></a>
          <a target="_blank" rel="noopener noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
             aria-label="Share on LinkedIn" className="h-7 w-7 rounded-full bg-slate-100 dark:bg-slate-800 grid place-items-center hover:bg-slate-900 hover:text-white"><Linkedin className="h-3.5 w-3.5" /></a>
          <button onClick={copyLink} aria-label="Copy link" className="h-7 w-7 rounded-full bg-slate-100 dark:bg-slate-800 grid place-items-center hover:bg-slate-900 hover:text-white"><Copy className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      {post.featured_image && (
        <div className="mt-8 aspect-[16/9] rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800">
          <img src={post.featured_image} alt={post.title} className="h-full w-full object-cover" />
        </div>
      )}

      <div data-testid="blog-content" className="prose-blog mt-10">
        {renderBlogWithEmbeds(post.content || "")}
      </div>

      {post.tags?.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-2">
          {post.tags.map(t => <span key={t} className="px-2.5 py-1 rounded-full text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">#{t}</span>)}
        </div>
      )}

      {post.related?.length > 0 && (
        <section className="mt-16">
          <h3 className="text-2xl font-extrabold display-serif mb-6">Related articles</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {post.related.map(r => <PostCard key={r.id} post={r} />)}
          </div>
        </section>
      )}
    </article>
  );
}

function setMeta(name, content, og = false) {
  const attr = og ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
